import { t } from 'src/i18n';
import { pageIds } from 'src/store/selectors/pages/pageIds';
import { hsePlanTemplateId } from 'src/store/selectors/templates';
import type { Promo } from './promo-type';
import type { FeatureStates } from './featureStates';
import { isEntityActive } from './features/isEntityActive';
import { isPageActive } from './features/isPageActive';
import type { EntityType } from './entityType';
import { getAvailableBundleUpgrades } from './getAvailableBundleUpgrades';

// De brukervendte tekstene (`title`, `text`, `actionText`, `pageName`) er
// implementert som lazy gettere slik at i18n-`t()` kjøres ved oppslag (kalltid)
// og ikke ved import. Spredning (`{ ...promoItem }`) leser getterne og kopierer
// de oppløste strengene, så forbrukere ser fortsatt vanlige string-felter.
export const promo: Promo[] = [
  {
    id: 'meetings',
    imageSource: '/promo-media/meetings.png',
    get title() {
      return t('promo.meetings.title');
    },
    get text() {
      return t('promo.meetings.text');
    },
    modules: ['MEETINGS'],
    bundles: ['MINI', 'BASIC', 'EXTRA'],
    addons: [],
    selectedFeature: 'MEETINGS',
    pageAfterUpgrade: 9772,
  },
  {
    id: 'issues',
    imageSource: '/promo-media/issues.png',
    videoSource: '/promo-media/issues.mp4',
    get title() {
      return t('promo.issues.title');
    },
    get text() {
      return t('promo.issues.text');
    },
    modules: ['ISSUES'],
    bundles: ['MINI', 'BASIC', 'EXTRA'],
    addons: [],
    selectedFeature: 'ISSUES',
    pageAfterUpgrade: 882901,
  },
  {
    id: 'risk',
    imageSource: '/promo-media/risk.png',
    get title() {
      return t('promo.risk.title');
    },
    get text() {
      return t('promo.risk.text');
    },
    modules: ['RISK'],
    bundles: ['MINI', 'BASIC', 'EXTRA'],
    addons: [],
    selectedFeature: 'RISK',
    pageAfterUpgrade: 712363,
  },
  {
    id: 'substances',
    imageSource: '/promo-media/substances.png',
    get title() {
      return t('promo.substances.title');
    },
    get text() {
      return t('promo.substances.text');
    },
    modules: ['SUBSTANCES'],
    bundles: ['BASIC', 'EXTRA'],
    addons: [],
    selectedFeature: 'SUBSTANCES',
    pageAfterUpgrade: 12368,
  },
  {
    id: 'staffing',
    imageSource: '/promo-media/staffing.png',
    get title() {
      return t('promo.staffing.title');
    },
    get text() {
      return t('promo.staffing.text');
    },
    modules: ['STAFFING'],
    bundles: ['MINI', 'BASIC', 'EXTRA'],
    addons: [],
    selectedFeature: 'STAFFING',
    pageAfterUpgrade: 5962,
  },
  {
    id: 'assets',
    imageSource: '/promo-media/assets.png',
    get title() {
      return t('promo.assets.title');
    },
    get text() {
      return t('promo.assets.text');
    },
    modules: ['ASSETS'],
    bundles: ['BASIC', 'EXTRA'],
    addons: [],
    selectedFeature: 'ASSETS',
    pageAfterUpgrade: 110,
  },
  {
    id: 'data',
    imageSource: '/promo-media/data.jpg',
    get title() {
      return t('promo.data.title');
    },
    get text() {
      return t('promo.data.text');
    },
    modules: ['DATA'],
    bundles: ['MINI', 'BASIC', 'EXTRA'],
    addons: [],
    selectedFeature: 'DATA',
    pageAfterUpgrade: 20,
  },
  {
    id: 'healthcare',
    imageSource: '/promo-media/complete.png',
    get title() {
      return t('promo.healthcare.title');
    },
    get text() {
      return t('promo.healthcare.text');
    },
    modules: ['HEALTHCARE'],
    bundles: [],
    addons: [],
    selectedFeature: 'HEALTHCARE',
    pageAfterUpgrade: 695944,
  },
  {
    id: 'norengros',
    imageSource: '/promo-media/norengros.png',
    get title() {
      return t('promo.norengros.title');
    },
    get text() {
      return t('promo.norengros.text');
    },
    modules: ['NORENGROS-NEW', 'NORENGROS-EXISTING'],
    bundles: [],
    addons: [],
    selectedFeature: 'NORENGROS',
    pageAfterUpgrade: 695944,
    get actionText() {
      return t('promo.norengros.actionText');
    },
  },
  {
    id: 'hse',
    imageSource: '/promo-media/complete.png',
    get title() {
      return t('promo.hse.title');
    },
    get text() {
      return t('promo.hse.text');
    },
    modules: [],
    bundles: ['MINI', 'BASIC', 'EXTRA'],
    addons: ['HEALTHCARE'],
    selectedFeature: 'BASIC',
    pageAfterUpgrade: 63,
  },
  {
    id: 'complete',
    imageSource: '/promo-media/complete.png',
    get title() {
      return t('promo.complete.title');
    },
    get text() {
      return t('promo.complete.text');
    },
    modules: [],
    bundles: ['MINI', 'BASIC', 'EXTRA'],
    addons: ['HEALTHCARE'],
    selectedFeature: 'BASIC',
    pageAfterUpgrade: 695944,
  },
  {
    id: 'doctypes',
    imageSource: '/promo-media/complete.png',
    get title() {
      return t('promo.doctypes.title');
    },
    get text() {
      return t('promo.doctypes.text');
    },
    modules: [],
    selectedFeature: 'BASIC',
    addons: ['HEALTHCARE'],
    bundles: ['MINI', 'BASIC', 'EXTRA'],
    pageAfterUpgrade: 695944,
  },
  {
    // Modul-promo (del 1): opprettelse av selve infoskjerm-modulen. Gratis –
    // derfor tom `modules` (ingen priset modul-opsjon) og forklaring i teksten
    // i stedet for pris. Handlingen dispatcher `add-features` med SIGNAGE.
    id: 'signage',
    imageSource: '/promo-media/screens.png',
    get title() {
      return t('promo.signage.title');
    },
    get text() {
      return t('promo.signage.text');
    },
    modules: [],
    bundles: [],
    addons: [],
    selectedFeature: 'SIGNAGE',
    pageAfterUpgrade: pageIds.screens,
    get actionText() {
      return t('promo.signage.actionText');
    },
  },
  {
    // Per-skjerm-bestilling (del 2): bestilling av en fysisk skjerm/mediespiller.
    // Dette er IKKE en modul, derfor egen promo-nøkkel. `modules: ['SIGNAGE']`
    // sørger for at per-skjerm-prisen (startPrice/organizationPrice) vises.
    // Handlingen håndteres av d-screen-view som dispatcher `place-screen-order`.
    id: 'screen-order',
    imageSource: '/promo-media/screens.png',
    get title() {
      return t('promo.screenOrder.title');
    },
    get text() {
      return t('promo.screenOrder.text');
    },
    modules: ['SIGNAGE_SCREEN'],
    bundles: [],
    addons: [],
    selectedFeature: 'SIGNAGE_SCREEN',
    pageAfterUpgrade: pageIds.screens,
    get actionText() {
      return t('promo.screenOrder.actionText');
    },
  },
];

export const menuPagesWithPromo: { pageId: number; pageName: string }[] = [
  {
    pageId: pageIds.events,
    get pageName() {
      return t('promo.menuPages.events');
    },
  },
  {
    pageId: pageIds.staffing,
    get pageName() {
      return t('promo.menuPages.staffing');
    },
  },
  {
    pageId: pageIds.events,
    get pageName() {
      return t('promo.menuPages.meetings');
    },
  },
  {
    pageId: pageIds.assets,
    get pageName() {
      return t('promo.menuPages.assets');
    },
  },
  {
    pageId: pageIds.substances,
    get pageName() {
      return t('promo.menuPages.substances');
    },
  },
  {
    pageId: pageIds.informationSecurity,
    get pageName() {
      return t('promo.menuPages.informationSecurity');
    },
  },
  {
    pageId: pageIds.issues,
    get pageName() {
      return t('promo.menuPages.issues');
    },
  },
  {
    pageId: pageIds.riskAssessments,
    get pageName() {
      return t('promo.menuPages.riskAssessments');
    },
  },
  {
    pageId: pageIds.screens,
    get pageName() {
      return t('promo.menuPages.screens');
    },
  },
];

function promoIdForEntity(
  entityType: string,
  item: { templateId?: number } | undefined = undefined,
): string | undefined {
  switch (entityType) {
    case 'assets':
      return 'assets';
    case 'collections':
      return item?.templateId === hsePlanTemplateId ? 'healthcare' : undefined;
    case 'constitutionalDocuments':
      return 'complete';
    case 'contacts':
      return 'complete';
    case 'contracts':
      return 'complete';
    case 'documents':
      return 'complete';
    case 'employees':
      return 'complete';
    case 'eventOccurrences':
      return 'complete';
    case 'functions':
      return 'complete';
    case 'guidelines':
      return 'complete';
    case 'issues':
      return 'issues';
    case 'meetingOccurrences':
      return 'meetings';
    case 'meetings':
      return 'meetings';
    case 'partners':
      return 'complete';
    case 'reports':
      return 'complete';
    case 'riskAssessments':
      return 'risk';
    case 'substances':
      return 'substances';
    case 'tasks':
      return 'complete';
    case 'tutorials':
      return 'complete';
  }
  return 'complete';
}

export function getPromoDataForEntity(
  entityType: EntityType,
  item: any,
  featureStates: FeatureStates,
  parentFunction: any = undefined,
  currentFeatures?: string[],
): Promo | undefined {
  if (!isEntityActive(entityType, item, featureStates, parentFunction)) {
    const id = promoIdForEntity(entityType, item);
    if (id) {
      const promoItem = promo.find((p) => {
        return p.id === id;
      });

      // Filter bundles based on current features if provided
      if (promoItem && currentFeatures && promoItem.bundles.length > 0) {
        const filteredBundles = getAvailableBundleUpgrades(currentFeatures, promoItem.bundles);
        return {
          ...promoItem,
          bundles: filteredBundles,
        };
      }

      return promoItem;
    }
  }
  return undefined;
}

export function getPromoCodeForPage(pageId: number): EntityType | undefined {
  switch (pageId) {
    case pageIds.status:
      return undefined;
    case pageIds.events:
      return 'complete';
    case pageIds.staffing:
      return 'staffing';
    case pageIds.meetings:
      return 'meetings';
    case pageIds.assets:
      return 'assets';
    case pageIds.substances:
      return 'substances';
    case pageIds.dataSystems:
      return 'data';
    case pageIds.issues:
      return 'issues';
    case pageIds.riskAssessments:
      return 'risk';
    case pageIds.screens:
      return 'signage';
    case pageIds.collections:
      return undefined;
    case pageIds.management:
      return 'complete';
    case pageIds.hse:
      return 'hse';
    case pageIds.business:
      return 'complete';
    case pageIds.assetManagement:
      return 'assets';
    case pageIds.informationSecurity:
      return 'data';
    case pageIds.quality:
      return 'complete';
    case pageIds.company:
      return undefined;
    case pageIds.employees:
      return 'complete';
    case pageIds.partners:
      return 'complete';
    case pageIds.people:
      /**
       * This page is only for the single module meetings, but it doesn’t make sense to display promo here
       */
      return undefined;
    case pageIds.tutorials:
      return 'complete';
    case pageIds.references:
      return 'complete';
    case pageIds.trash:
      return undefined;
    default:
      return undefined;
  }
}

export function getpromoDataForPage(
  pageId: number,
  featureStates: FeatureStates,
  singleUserVersion: boolean,
  currentFeatures?: string[],
): Promo | undefined {
  if (!isPageActive(pageId, featureStates, singleUserVersion)) {
    const promoCode = getPromoCodeForPage(pageId);
    if (promoCode) {
      const promoItem = promo.find((p) => {
        return p.id === promoCode;
      });

      // Filter bundles based on current features if provided
      if (promoItem && currentFeatures && promoItem.bundles.length > 0) {
        const filteredBundles = getAvailableBundleUpgrades(currentFeatures, promoItem.bundles);
        return {
          ...promoItem,
          bundles: filteredBundles,
        };
      }

      return promoItem;
    }
  }
  return undefined;
}
